"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAddressesColumns = void 0;
const react_1 = __importDefault(require("react"));
const ecommerce_1 = require("../../api/ecommerce");
const Button_1 = __importDefault(require("../../common/js/atoms/Button"));
const GlobalModal_1 = require("../../common/js/components/GlobalModal");
const ScopeGuard_1 = __importDefault(require("../../common/js/components/security/ScopeGuard"));
const AuthorizationScopes_1 = __importDefault(require("../../common/js/enums/AuthorizationScopes"));
const ButtonSize_1 = __importDefault(require("../../common/js/enums/ButtonSize"));
const ButtonVariant_1 = __importDefault(require("../../common/js/enums/ButtonVariant"));
const Zupicons_1 = __importDefault(require("../../common/js/enums/Zupicons"));
const string_1 = require("../../common/js/helper/string");
const translation_1 = require("../../common/js/helper/translation");
const ChangeRequestModal_1 = __importDefault(require("../components/ChangeRequestModal"));
const DeleteAddressConfirmModal_1 = __importDefault(require("../components/DeleteAddressConfirmModal"));
const MessageModal_1 = __importDefault(require("../components/MessageModal"));
const MassiveCompany_1 = __importDefault(require("../stores/MassiveCompany"));
const EmptyColumn = () => ({
    allowOverflow: true,
    name: '',
    selector: () => '',
    sortable: false,
    minWidth: '0',
    maxWidth: '0',
});
const CompanyNameColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.company_name.column_name'),
    sortable: true,
    selector: (address) => `${address.name1}${!!address.name2 ? ', ' + address.name2 : ''}`,
    format: (address) => {
        let name = address.name1;
        if (!!address.name2) {
            name += `, ${address.name2}`;
        }
        return react_1.default.createElement("div", { className: '--white-space-normal my-2' }, name);
    },
    minWidth: '16.25rem',
});
const StreetColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.street.column_name'),
    selector: (address) => address.street ?? '',
    format: (address) => {
        return react_1.default.createElement("div", { className: '--white-space-normal my-2' }, address.street);
    },
    minWidth: '16.25rem',
});
const ZipColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.zip.column_name'),
    selector: (address) => address.zipcode,
    minWidth: '10rem',
});
const CityColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.city.column_name'),
    selector: (address) => address.city,
});
const DefaultAddressColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.company_address.column_name'),
    sortable: true,
    selector: (address) => MassiveCompany_1.default.company?.defaultMassiveAddressId === address.id ? 1 : 0,
    format: (address) => {
        if (MassiveCompany_1.default.company?.defaultMassiveAddressId === address.id) {
            return react_1.default.createElement("i", { className: 'znd-user-profile-page__bullet' });
        }
        return react_1.default.createElement("div", { className: 'znd-user-profile-page__empty' }, "-");
    },
    minWidth: '10rem',
});
const DefaultShippingAddressColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.default_shipping_address.column_name'),
    sortable: true,
    selector: (address) => MassiveCompany_1.default.company?.defaultShippingMassiveAddressId === address.id ? 1 : 0,
    format: (address) => {
        if (MassiveCompany_1.default.company?.defaultShippingMassiveAddressId === address.id) {
            return react_1.default.createElement("i", { className: 'znd-user-profile-page__check icon-check' });
        }
        return react_1.default.createElement("div", { className: 'znd-user-profile-page__empty' }, "-");
    },
    minWidth: '10rem',
});
const DefaultBillingAddressColumn = () => ({
    allowOverflow: true,
    name: (0, translation_1.t)('user.profile.addresses.table.default_billing_address.column_name'),
    sortable: true,
    selector: (address) => MassiveCompany_1.default.company?.defaultBillingMassiveAddressId === address.id ? 1 : 0,
    format: (address) => {
        if (MassiveCompany_1.default.company?.defaultBillingMassiveAddressId === address.id) {
            return react_1.default.createElement("i", { className: 'znd-user-profile-page__check icon-check' });
        }
        return react_1.default.createElement("div", { className: 'znd-user-profile-page__empty' }, "-");
    },
    minWidth: '10rem',
});
const ActionsColumn = () => ({
    allowOverflow: true,
    name: '',
    sortable: false,
    cell: (address) => {
        if (!!address.dataUpdateTriggeredAt) {
            return react_1.default.createElement("span", { className: "t-body-small mr-4" }, (0, translation_1.t)('user.profile.addresses.in_progress'));
        }
        const isDefaultAddress = MassiveCompany_1.default.company?.defaultMassiveAddressId === address.id;
        const isDefaultBillingAddress = MassiveCompany_1.default.company?.defaultBillingMassiveAddressId === address.id;
        const isDefaultShippingAddress = MassiveCompany_1.default.company?.defaultShippingMassiveAddressId === address.id;
        const changeRequestData = {
            additionalAddressLine: (0, string_1.ensureNonEmptyStringOrUndefined)(address?.additionalAddressLine),
            city: address.city,
            countryCode: MassiveCompany_1.default.company?.defaultMassiveAddress?.country?.iso,
            countryStateCode: address?.countryState?.shortCode
                ? address?.countryState?.shortCode.substring(3)
                : undefined,
            department: undefined,
            email: address.email,
            fax: (0, string_1.ensureNonEmptyStringOrUndefined)(address?.fax),
            name1: address.name1,
            name2: address.name2,
            phoneNumber1: (0, string_1.ensureNonEmptyStringOrUndefined)(address?.phoneNumber),
            phoneNumber2: undefined,
            role: undefined,
            salutation: undefined,
            street: (0, string_1.ensureNonEmptyStringOrUndefined)(address?.street),
            website: (0, string_1.ensureURLWithHTTPS)((0, string_1.ensureNonEmptyStringOrUndefined)(address?.website)),
            zipCode: address.zipcode,
            isDefaultAddress,
            isDefaultBillingAddress,
            isDefaultShippingAddress,
            taxId: (0, string_1.ensureNonEmptyStringOrUndefined)(MassiveCompany_1.default.company?.vatId),
            externalId: address?.externalId,
        };
        return (react_1.default.createElement("div", { className: "d-flex mr-4" },
            react_1.default.createElement(ScopeGuard_1.default, { scopes: [AuthorizationScopes_1.default.COMPANY_UPDATE, AuthorizationScopes_1.default.ADDRESS_UPDATE] },
                react_1.default.createElement(Button_1.default, { className: "znd-user-profile-page__button", variant: ButtonVariant_1.default.text, size: ButtonSize_1.default.default, text: (0, translation_1.t)('user.profile.addresses.edit_address.button'), iconRight: Zupicons_1.default.pen, onClick: () => {
                        (0, GlobalModal_1.showModal)({
                            component: MessageModal_1.default,
                            props: {
                                title: (0, translation_1.t)('user.profile.addresses.edit_address.modal.title'),
                                body: (0, translation_1.t)('user.profile.addresses.edit_address.modal.confirm.text'),
                            },
                        }).then(() => {
                            (0, GlobalModal_1.showModal)({
                                component: ChangeRequestModal_1.default,
                                props: {
                                    title: (0, translation_1.t)('user.profile.addresses.edit_address.modal.title'),
                                    edit: true,
                                    topic: ecommerce_1.ChangeRequestTopicEnum.Address,
                                    object: ecommerce_1.ChangeRequestObjectEnum.AddressAddress,
                                    data: changeRequestData,
                                },
                            }).then(() => {
                                MassiveCompany_1.default.loadCompany();
                            });
                        });
                    } })),
            !isDefaultAddress && !isDefaultBillingAddress && !isDefaultShippingAddress ? (react_1.default.createElement(ScopeGuard_1.default, { scopes: [AuthorizationScopes_1.default.COMPANY_UPDATE, AuthorizationScopes_1.default.ADDRESS_DELETE] },
                react_1.default.createElement(Button_1.default, { className: 'znd-addresses-table-sub__button', variant: ButtonVariant_1.default.text, size: ButtonSize_1.default.default, text: '', iconRight: Zupicons_1.default.trash, onClick: () => {
                        (0, GlobalModal_1.showModal)({
                            component: DeleteAddressConfirmModal_1.default,
                            props: {
                                externalId: address?.externalId ?? '',
                            },
                        }).then(() => {
                            MassiveCompany_1.default.loadCompany();
                        });
                    } }))) : null));
    },
    minWidth: '12.5rem',
    right: true,
});
const getAddressesColumns = () => [
    EmptyColumn(),
    CompanyNameColumn(),
    StreetColumn(),
    ZipColumn(),
    CityColumn(),
    DefaultAddressColumn(),
    DefaultBillingAddressColumn(),
    DefaultShippingAddressColumn(),
    ActionsColumn(),
];
exports.getAddressesColumns = getAddressesColumns;
